import * as RadixPortal from '@radix-ui/react-portal'
import { useAtom } from '@reatom/npm-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Map } from '@blade/std-lib'

import { PlusFloatingButton } from './common/plus-floating-button'
import { ConnectorNode } from './connector-node'
import { EdgeLine } from './edge-line'
import { PlaceholderNode } from './placeholder-node'
import { WalletDestinationNode } from './wallet-destination-node'
import { WalletDestinationSelectionDialog } from './wallet-destination-selection-dialog'

import type { DistributeFundsModel } from '../../atoms'
import type { WalletModel } from '@root/entities/wallet'

type WalletDestinationNodesProps = {
  model: DistributeFundsModel
  rootEl: React.RefObject<HTMLDivElement | null>
  amountInputNodeRef: React.RefObject<HTMLDivElement | null>
}

export const WalletDestinationNodes: React.FC<WalletDestinationNodesProps> = ({
  model,
  amountInputNodeRef,
  rootEl,
}) => {
  const { t } = useTranslation()

  const [connectorEl, setConnectorEl] = useState<HTMLDivElement | undefined>(
    undefined,
  )
  const [targets] = useAtom(model.targets)
  const [open, setOpen] = useState(false)
  const [placeholderEl, setPlaceholderEl] = useState<
    HTMLDivElement | undefined
  >(undefined)

  const targetElementsRef = useRef<Map<WalletModel, HTMLDivElement>>(
    Map.create(),
  )

  const [edges, setEdges] = useState<Array<React.ReactElement>>([])

  useEffect(() => {
    const edges: Array<React.ReactElement> = []
    if (!connectorEl) {
      return
    }
    setTimeout(() => {
      for (const target of targets) {
        const element = targetElementsRef.current.get(target.wallet)
        if (element) {
          edges.push(
            <EdgeLine
              key={target.wallet.id}
              sourceEl={connectorEl}
              targetEl={element}
              rootEl={rootEl}
              sourcePosition="center"
              targetPosition="top-middle"
              direction="horizontal-first"
            />,
          )
        }
      }
      setEdges(edges)
    })
  }, [amountInputNodeRef, connectorEl, rootEl, targets])

  return (
    <>
      <RadixPortal.Root
        asChild
        container={document.getElementById('edges-container')}
      >
        <>
          {connectorEl ? (
            <EdgeLine
              sourceEl={amountInputNodeRef}
              targetEl={connectorEl}
              rootEl={rootEl}
              sourcePosition="bottom-middle"
              targetPosition="center"
              direction="vertical-first"
              variant={targets.length > 0 ? 'default' : 'placeholder'}
            />
          ) : undefined}
          {placeholderEl ? (
            <EdgeLine
              sourceEl={amountInputNodeRef}
              targetEl={placeholderEl}
              rootEl={rootEl}
              sourcePosition="bottom-middle"
              targetPosition="top-middle"
              direction="vertical-first"
              variant="placeholder"
            />
          ) : undefined}
          {edges}
        </>
      </RadixPortal.Root>
      <EdgesPlaceholder>
        {targets.length > 0 ? (
          <>
            <PlusFloatingButton
              onClick={() => {
                setOpen(true)
              }}
            />
            <ConnectorNode
              style={{ position: 'absolute', top: 26, left: `calc(50% - 2px)` }}
              ref={(el) => setConnectorEl(el ?? undefined)}
            />
          </>
        ) : undefined}
      </EdgesPlaceholder>
      <Container>
        {targets.length > 0 ? (
          targets.map((target) => (
            <WalletDestinationNode
              key={target.wallet.id}
              target={target}
              model={model}
              ref={(el) => {
                if (el) {
                  const map = targetElementsRef.current
                  map.set(target.wallet, el)

                  return () => {
                    const map = targetElementsRef.current
                    map.delete(target.wallet)
                  }
                }
              }}
            />
          ))
        ) : (
          <PlaceholderNode
            ref={(el) => setPlaceholderEl(el ?? undefined)}
            label={t('wallet.distribute.add-wallet')}
            onClick={() => {
              setOpen(true)
            }}
          />
        )}
      </Container>

      <WalletDestinationSelectionDialog
        model={model}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}

const Container = styled.div`
  display: grid;
  gap: 10px;
  justify-content: center;
  grid-template-columns: repeat(auto-fit, 110px);
`

const EdgesPlaceholder = styled.div`
  height: 50px;
  position: relative;

  display: grid;
  grid-auto-flow: column;
  justify-items: center;
`
